import type { RawSearchResult, SearchParams } from '../types';

export interface SolrResponse<T = {}> {
  response: { numFound?: number; docs?: T[]; start?: number };
  facets?: unknown[];
}

/**
 * Make a solr query, terminating other pending fetch requests that may exist.
 */
export default (() => {
  let abortController = new AbortController();
  let pending = false;

  return async function getSolrResult(
    url: string,
    search: Partial<SearchParams>
  ): Promise<SolrResponse<RawSearchResult> | null> {
    if (pending) {
      abortController.abort();
      abortController = new AbortController();
    }

    const q = typeof search.query === 'string' ? search.query.trim() : '*';

    const solrParams = {
      offset: search.offset,
      limit: search.limit,
      filter: search.filter,
      fields: search.fetchFields,
      sort: search.sort + ' ,ss_field_report_number desc',
      params: search.query
        ? {
            q,
            defType: 'edismax',
            qf:
              'tm_X3b_und_auto_aggregated_fulltext_field tm_X3b_en_auto_aggregated_fulltext_field',
            fl:
              'tm_X3b_und_title,tm_X3b_en_title,tm_X3b_und_auto_aggregated_fulltext_field,tm_X3b_en_auto_aggregated_fulltext_field,*,score',
            wt: 'json'
          }
        : {
            q,
            defType: 'edismax',
            qf:
              'tm_X3b_und_auto_aggregated_fulltext_field tm_X3b_en_auto_aggregated_fulltext_field',
            fl:
              'tm_X3b_und_title,tm_X3b_en_title,tm_X3b_und_auto_aggregated_fulltext_field,tm_X3b_en_auto_aggregated_fulltext_field,*,score',
            wt: 'json'
          }
    };

    const body = JSON.stringify(solrParams);

    pending = true;

    const response = await fetch(url, {
      method: 'post',
      body,
      headers: { 'Content-Type': 'application/json' },
      signal: abortController.signal
    });

    if (response.ok) {
      try {
        const json = await response.json();
        return json;
      } catch (e) {
        if (e.name === 'AbortError') {
          return null;
        } else {
          throw new Error(e.message);
        }
      }
    } else {
      throw new Error([response.status, response.statusText].join(' '));
    }
  };
})();
